/* eslint-disable react/no-unknown-property */
import React from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import { shareService } from '../domain/services/shareService';
import { ucFirst, removeHtml } from '../domain/utils/stringUtils';
import { IconFacebook } from './Icon/IconFacebook';
import { IconFlipboard } from './Icon/IconFlipboard';
import { IconWhatsapp } from './Icon/IconWhatsapp';
import { IconTwitter } from './Icon/IconTwitter';
import { IconYoutube } from './Icon/IconYoutube';
import { IconInstagram } from './Icon/IconInstagram';
import { IconEmail } from './Icon/IconEmail';
import { IconRss } from './Icon/IconRss';
import { theme } from '../domain/theme';

const hidden = css`
  display: none;
`;

const sharelink = css`
  &:hover {
    color: #fc0 !important;
  }
  &.sharelink--facebook {
    color: #3c5a99;
  }
  &.sharelink--flipboard {
    color: #f52828;
  }
  &.sharelink--instagram {
    color: #fff;
  }
  &.sharelink--mail {
    color: #fff;
  }
  &.sharelink--twitter {
    color: #1da1f2;
  }
  &.sharelink--whatsapp {
    color: #455b63;
  }
  &.sharelink--youtube {
    color: #cd201f;
  }
`;

const ShareLink = ({
  type,
  link,
  mobile,
  url,
  title,
  via,
  gtmData,
  color,
  popUp,
  useBrowserUrl
}) => {
  let shareUrl;
  const fullUrl = encodeURIComponent(url);
  const cleanTitle = removeHtml(title);
  const encodedTitle = encodeURIComponent(ucFirst(cleanTitle || type));
  const gtmDataProps = {};
  if (gtmData.length > 0) {
    gtmData.forEach(value => {
      if (value.var) {
        gtmDataProps[`data-${value.var}`] = value.value;
      }
    });
  }
  const openHandler = e => {
    if (popUp) {
      e.preventDefault();

      if (useBrowserUrl) {
        shareUrl = shareService.getShareUrl({
          type,
          url: null,
          encodedTitle,
          via,
          mobile
        });
      }

      window.open(
        shareUrl,
        'socialShareWindow',
        `width=550,height=500,top=${window.screen.height / 2 - 250}`
      );
    }
  };
  // If the URL is a link only (not sharing), and the link isn't
  // a mail type, then the share url will be whatever
  // is passed into the component.
  if (link && type !== 'mail') {
    shareUrl = url;
  } else {
    // Otherwise, construct the share url differently depending on the type
    // of sharing.
    shareUrl = shareService.getShareUrl({
      type,
      url: fullUrl,
      encodedTitle,
      via,
      mobile
    });
  }
  const styleShareLink = css`
    display: block;
    margin-right: 13px;
    margin-bottom: 13px;
    ${theme.mq.small_desktop} {
      margin-right: 0;
    }
    svg {
      display: block;
    }
    &:hover {
      svg {
        fill: #fc0;
      }
    }
  `;

  let IconComponent;
  switch (type) {
    case 'facebook':
      IconComponent = IconFacebook;
      break;
    case 'flipboard':
      IconComponent = IconFlipboard;
      break;
    case 'whatsapp':
      IconComponent = IconWhatsapp;
      break;
    case 'twitter':
      IconComponent = IconTwitter;
      break;
    case 'youtube':
      IconComponent = IconYoutube;
      break;
    case 'instagram':
      IconComponent = IconInstagram;
      break;
    case 'email':
      IconComponent = IconEmail;
      break;
    case 'rss':
      IconComponent = IconRss;
      break;
    default:
      throw new Error(`Icon type ${type} not found.`);
  }

  return (
    <a
      css={[styleShareLink, sharelink]}
      href={shareUrl}
      data-action={
        shareUrl.indexOf('whatsapp') !== -1 ? 'share/whatsapp/share' : null
      }
      target="_blank"
      rel="noopener noreferrer"
      className={`sharelink sharelink--${type}`}
      title={cleanTitle}
      {...gtmDataProps}
      data-no-external-disclaimer
      onClick={openHandler}
    >
      <IconComponent color={color} size={20} />
      <span className="hidden" css={hidden} arial-label={type} role="button">
        {type}
      </span>
    </a>
  );
};

ShareLink.defaultProps = {
  link: false,
  mobile: false,
  title: undefined,
  via: undefined,
  gtmData: [],
  color: undefined,
  popUp: true,
  useBrowserUrl: false
};

ShareLink.propTypes = {
  type: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  link: PropTypes.bool,
  mobile: PropTypes.bool,
  title: PropTypes.string,
  via: PropTypes.string,
  gtmData: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  color: PropTypes.string,
  popUp: PropTypes.bool,
  useBrowserUrl: PropTypes.bool
};

export default ShareLink;
